import React from 'react';
import { 
    Sidebar, 
    Menu, 
    useSidebarState 
} from 'react-admin';
import { 
    Box, 
    Typography, 
    Link, 
    Divider,
    useTheme,
    alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled container for the footer
const SidebarFooter = styled(Box)(({ theme, open }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1.5),
    backgroundColor: alpha(theme.palette.background.default, 0.8),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: theme.transitions.create(['padding', 'opacity'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter,
    }),
    // Adjust padding and content based on sidebar state
    ...(open ? {
        opacity: 1,
        padding: theme.spacing(1.5),
    } : {
        opacity: 0.8,
        padding: theme.spacing(1, 0.5),
    }),
}));

// Custom link styling
const FooterLink = styled(Link)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    fontSize: '0.75rem',
    fontWeight: 400,
    '&:hover': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
    },
    '&:not(:last-child)::after': {
        content: '" | "',
        margin: '0 4px',
        color: theme.palette.text.disabled,
    },
}));

const CustomSidebar = ({ ...props }) => {
    const theme = useTheme();
    const [sidebarOpen] = useSidebarState();

    const handlePrivacyClick = () => {
        window.location.hash = '#/privacy-policy';
    };
    
    const handleTermsClick = () => {
        window.location.hash = '#/terms-of-service';
    };
    
    const handleCookieSettingsClick = () => {
        window.location.hash = '#/cookie-settings';
    };

    return (
        <Sidebar {...props}>
            {/* Regular menu items */}
            <Menu>
                <Menu.DashboardItem />
                <Menu.ResourceItem name="provenance" />
                <Menu.ResourceItem name="verifications" />
                <Menu.ResourceItem name="marketing" />
                <Menu.ResourceItem name="nft" />
                {/* Legal pages intentionally excluded from main menu */}
            </Menu>
            
            {/* GDPR Footer */}
            <SidebarFooter open={sidebarOpen}>
                {sidebarOpen ? (
                    // Full view when sidebar is open
                    <Box>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                display: 'block',
                                color: 'text.secondary',
                                mb: 0.5,
                                fontSize: '0.7rem'
                            }}
                        >
                            Data & Privacy
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <FooterLink 
                                component="button"
                                onClick={handlePrivacyClick}
                                sx={{ 
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            >
                                Privacy Policy
                            </FooterLink>
                            <FooterLink 
                                component="button"
                                onClick={handleTermsClick}
                                sx={{ 
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            >
                                Terms
                            </FooterLink>
                            <FooterLink 
                                component="button"
                                onClick={handleCookieSettingsClick}
                                sx={{ 
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            >
                                Cookie Settings
                            </FooterLink>
                        </Box>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                display: 'block',
                                color: 'text.disabled',
                                mt: 0.5,
                                fontSize: '0.65rem'
                            }}
                        >
                            © 2025 ViV Wine & Spirits
                        </Typography>
                    </Box>
                ) : (
                    // Minimal view when sidebar is collapsed
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.6rem',
                                writingMode: 'vertical-rl',
                                textOrientation: 'mixed'
                            }}
                        >
                            Privacy
                        </Typography>
                    </Box>
                )}
            </SidebarFooter>
        </Sidebar>
    );
};

export default CustomSidebar;